import React, { useState } from 'react';
import { BookOpen, Boxes, Workflow, Database, Code2, Search, HelpCircle, ChevronDown, ShieldCheck, Cpu, Mail } from 'lucide-react';
import { useLang } from '../i18n';
import { Card, Badge, Reveal, Input } from '../components/ui';

const SECTIONS = [
  { k: 'overview', label: 'Overview', icon: BookOpen },
  { k: 'features', label: 'Features', icon: Boxes },
  { k: 'architecture', label: 'Architecture', icon: Workflow },
  { k: 'model', label: 'Data Model', icon: Database },
  { k: 'api', label: 'API Reference', icon: Code2 },
  { k: 'faq', label: 'FAQ', icon: HelpCircle },
];

const API = [
  ['Auth', [
    ['POST', '/api/auth/register', 'Create a customer / fleet account'],
    ['POST', '/api/auth/login', 'Sign in, returns JWT'],
    ['GET', '/api/auth/me', 'Current user'],
  ]],
  ['Buy', [
    ['POST', '/api/vehicles/lookup', 'Authorized registry lookup by plate'],
    ['POST', '/api/vehicles/ocr', 'OCR extract from registration image'],
    ['POST', '/api/insurance/match', 'AI coverage match + explanation'],
    ['POST', '/api/quotes', 'Deterministic premium quote'],
    ['POST', '/api/payment/simulate', 'Issue policy (scoped to user)'],
  ]],
  ['Carry', [
    ['GET', '/api/policies', 'List my policies (+renewal status)'],
    ['POST', '/api/policies/ask', 'Grounded policy RAG Q&A'],
    ['POST', '/api/policies/{id}/renew', 'One-tap renewal'],
  ]],
  ['Claim', [
    ['POST', '/api/claims/start', 'Open a claim'],
    ['POST', '/api/claims/{id}/intake', 'AI conversational intake'],
    ['POST', '/api/claims/{id}/evidence', 'CV damage detection on a photo'],
    ['POST', '/api/claims/{id}/assess', 'Repair estimate + coverage + risk'],
    ['POST', '/api/claims/{id}/decide', 'Deterministic decision engine'],
    ['POST', '/api/claims/{id}/settlement/advance', 'Advance repair stage'],
  ]],
  ['Fleet & Admin', [
    ['POST', '/api/fleet/quote', 'Multi-vehicle quote + discount'],
    ['GET', '/api/fleets', 'Fleet dashboard data'],
    ['GET', '/api/admin/metrics', 'Admin KPIs'],
    ['POST', '/api/cron/renewal-reminders', 'Daily cron (Bearer secured)'],
  ]],
];

const FAQ = [
  { cat: 'Buying', qs: [
    ['Do I need to fill a long form to buy?', 'No. Enter your vehicle registration number (or scan the document) and AI pre-fills the details — you just confirm.'],
    ['How is my premium calculated?', 'Premiums come from a deterministic pricing engine using your vehicle class, usage, value and seats. AI never invents prices.'],
    ['What is mandatory vs optional cover?', 'Compulsory third-party liability (TNDS) is required by law; comprehensive own-damage, personal accident and cargo are optional add-ons.'],
  ]},
  { cat: 'Policies & Renewal', qs: [
    ['Where is my digital policy?', 'In the Carry wallet — with your certificate number, coverage and validity. You can ask the AI assistant plain-language questions grounded in your wording.'],
    ['How do renewal reminders work?', 'A daily job checks expiry; policies within 30 days trigger an in-app notification and an email, with a one-tap renew link.'],
    ['Will my premium change at renewal?', 'The renewal recomputes on your (now older) vehicle value. Your coverage set carries over unchanged; the exact renewal premium is shown when you confirm.'],
  ]},
  { cat: 'Claims', qs: [
    ['How do I file a claim?', 'Tap Claim, confirm everyone is safe, describe what happened in your words, then follow AI-guided photo capture. Low-risk claims can auto-approve.'],
    ['Why was my repair shown as 0₫?', 'Your policy likely carries third-party liability only (no comprehensive own-damage cover). A banner explains this; add comprehensive cover at renewal.'],
    ['When is a claim escalated to a human?', 'When there are injuries, the cost exceeds the auto threshold, coverage is unclear, fraud signals are high, or AI confidence is low.'],
  ]},
  { cat: 'Accounts & Roles', qs: [
    ['What roles exist?', 'Customer, Fleet Operator, Adjuster (Ops) and Administrator — each sees only what their role permits.'],
    ['Is my data private to me?', 'Yes. Policies, claims and notifications are scoped to your account; adjusters and admins have operational access for their duties.'],
    ['How do emails reach me?', 'Renewal and claim-status emails route to your account email, falling back to the notification-bell test recipient if unset.'],
  ]},
];

export default function Docs() {
  const { t } = useLang();
  const [sec, setSec] = useState('overview');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState({});

  const ql = q.trim().toLowerCase();
  const faqFiltered = FAQ.map((g) => ({
    ...g, qs: g.qs.filter(([a, b]) => !ql || a.toLowerCase().includes(ql) || b.toLowerCase().includes(ql)),
  })).filter((g) => g.qs.length);

  return (
    <div className="max-w-6xl mx-auto px-5 py-10" data-testid="docs-page">
      <Reveal>
        <div className="kicker">DOCUMENTATION</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-2">Tasco Platform Docs</h1>
        <p className="text-slate-400 mt-2">Architecture, API, features and FAQ for the AI-native vehicle insurance platform.</p>
      </Reveal>

      <div className="grid lg:grid-cols-5 gap-6 mt-8">
        <div className="lg:col-span-1 flex lg:flex-col gap-1 overflow-x-auto">
          {SECTIONS.map((s) => (
            <button key={s.k} onClick={() => setSec(s.k)} data-testid={`docs-nav-${s.k}`}
              className={`px-3 py-2.5 rounded-xl text-sm flex items-center gap-2 whitespace-nowrap ${sec === s.k ? 'bg-jade/10 text-jade' : 'text-slate-400 hover:text-white'}`}>
              <s.icon className="w-4 h-4" /> {s.label}
            </button>
          ))}
        </div>

        <div className="lg:col-span-4 space-y-5">
          {sec === 'overview' && (
            <Card className="p-6" data-testid="docs-overview">
              <h2 className="font-display text-2xl font-bold mb-3">Digital → Autonomous insurance</h2>
              <p className="text-slate-300 leading-relaxed">Tasco is a vehicle-centric insurance platform for Vietnam built around three moments — <b className="text-jade">Buy</b>, <b className="text-jade">Carry</b> and <b className="text-jade">Claim</b> — tied together by one AI agent that understands your vehicle, policy and history.</p>
              <div className="grid sm:grid-cols-3 gap-3 mt-5">
                {[[ShieldCheck, 'Auditable by design', 'Every automated decision records evidence, model outputs, confidence and rules.'],
                  [Cpu, 'AI perceives, rules decide', 'AI extracts/classifies/explains; deterministic engines own price, coverage & decisions.'],
                  [Mail, 'Proactive & connected', 'Renewal reminders and claim-status updates via in-app feed + email.']].map(([Ic, h, b], i) => (
                  <div key={i} className="card p-4"><Ic className="w-5 h-5 text-jade mb-2" /><div className="font-semibold text-sm">{h}</div><div className="text-xs text-slate-400 mt-1">{b}</div></div>
                ))}
              </div>
            </Card>
          )}

          {sec === 'features' && (
            <Card className="p-6" data-testid="docs-features">
              <h2 className="font-display text-xl font-bold mb-4">Feature catalogue</h2>
              {[['Buy', 'Plate lookup + OCR, AI vehicle→insurance matching, deterministic quote, simulated payment, digital policy'],
                ['Carry', 'Policy wallet, certificate, grounded AI policy Q&A, renewal nudges & one-tap renew'],
                ['Claim', 'Conversational intake, CV damage detection, repair estimate, coverage & risk checks, auto-approve / escalate, settlement tracker'],
                ['Fleet', 'Multi-vehicle onboarding with tiered discounts + operator dashboard with per-vehicle claims'],
                ['Ops', 'Adjuster review queue, alert feed, manual decisions with full evidence'],
                ['Admin', 'Metrics, customers, policies, configuration, tickets, feedback, finance & user management'],
                ['Platform', 'JWT roles, 4 themes, bilingual EN/VI, notifications + Resend email, daily cron']].map(([h, b]) => (
                <div key={h} className="flex gap-3 py-2.5 border-b border-white/5">
                  <Badge className="shrink-0 h-fit">{h}</Badge><span className="text-sm text-slate-300">{b}</span>
                </div>
              ))}
            </Card>
          )}

          {sec === 'architecture' && (
            <Card className="p-6" data-testid="docs-architecture">
              <h2 className="font-display text-xl font-bold mb-4">Architecture</h2>
              <div className="text-xs text-slate-500 mb-2">Prototype (this app) — React + FastAPI + MongoDB; target production maps to AWS.</div>
              <pre className="font-mono text-[11px] leading-relaxed text-slate-300 bg-ink/60 border border-white/10 rounded-xl p-4 overflow-x-auto">{`┌────────────────────────────────────────────────┐
│  React SPA  (Buy · Carry · Claim · Fleet · Ops · Admin)
│  JWT in localStorage → Authorization: Bearer
└───────────────┬────────────────────────────────┘
                │  HTTPS /api/*
        ┌───────▼─────────┐
        │  FastAPI         │  perception vs decision split
        │  ├ ai_service    │ ── GPT-5.6 Terra (OCR, vision, RAG, agent)
        │  ├ pricing/rules │ ── deterministic engines (price, coverage, decide)
        │  ├ auth (JWT)    │ ── roles: customer/fleet/adjuster/admin
        │  └ email_service │ ── Resend proxy (guardrailed)
        └───────┬──────────┘
      ┌─────────▼─────────┐   ┌──────────────┐
      │  MongoDB           │   │ Daily cron    │→ renewal reminders
      │  users, policies,  │   └──────────────┘
      │  claims, audit_log │
      └────────────────────┘

Production target → CloudFront+WAF · ECS FastAPI · RDS PostgreSQL ·
S3 (KMS, Object-Lock evidence) · Bedrock/SageMaker · Cognito`}</pre>
              <div className="mt-4 text-sm text-slate-300"><b className="text-jade">Core principle:</b> AI never invents prices or mandatory rules. It perceives and explains; deterministic, versioned engines produce every quote and every claim decision, and the audit log captures the full reasoning trail.</div>
            </Card>
          )}

          {sec === 'model' && (
            <Card className="p-6" data-testid="docs-model">
              <h2 className="font-display text-xl font-bold mb-4">Data model</h2>
              {[['users', 'id, email, name, role, password_hash'],
                ['vehicles', 'id, registration_no, make, model, year, vehicle_class, usage, seats, estimated_value_vnd'],
                ['policies', 'id, policy_no, owner_user_id, vehicle, products[], premium_vnd, fleet_id, status, start/end_date'],
                ['claims', 'id, claim_no, owner_user_id, policy_id, incident{}, evidence[], assessment{}, decision{}, settlement_tracking{}'],
                ['notifications', 'id, audience, owner_user_id, type, title, body, read, email_sent'],
                ['tickets / feedback', 'id, owner_user_id, subject/message/rating, status'],
                ['audit_log', 'id, claim_id, event, payload, timestamp (append-only)']].map(([h, b]) => (
                <div key={h} className="py-2.5 border-b border-white/5">
                  <span className="font-mono text-jade text-sm">{h}</span>
                  <div className="text-xs text-slate-400 mt-0.5 font-mono">{b}</div>
                </div>
              ))}
            </Card>
          )}

          {sec === 'api' && (
            <div className="space-y-4" data-testid="docs-api">
              {API.map(([group, rows]) => (
                <Card key={group} className="p-5">
                  <div className="kicker mb-3">{group}</div>
                  <div className="space-y-1.5">
                    {rows.map(([m, path, desc]) => (
                      <div key={path} className="flex items-center gap-3 text-sm py-1 border-b border-white/5">
                        <span className={`font-mono text-[10px] px-2 py-0.5 rounded ${m === 'GET' ? 'text-jade bg-jade/10' : 'text-amber bg-amber/10'}`}>{m}</span>
                        <span className="font-mono text-xs text-slate-200">{path}</span>
                        <span className="text-xs text-slate-500 ml-auto hidden sm:block">{desc}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {sec === 'faq' && (
            <div data-testid="docs-faq">
              <div className="relative mb-4">
                <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="faq-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions…" className="!pl-9" />
              </div>
              {faqFiltered.length === 0 && <Card className="p-6 text-slate-400 text-sm">No questions match “{q}”.</Card>}
              <div className="space-y-5">
                {faqFiltered.map((g) => (
                  <div key={g.cat} data-testid={`faq-cat-${g.cat}`}>
                    <div className="kicker mb-2">{g.cat}</div>
                    <div className="space-y-2">
                      {g.qs.map(([a, b], i) => {
                        const id = `${g.cat}-${i}`;
                        const isOpen = open[id] ?? !!ql;
                        return (
                          <Card key={id} className="overflow-hidden">
                            <button onClick={() => setOpen({ ...open, [id]: !isOpen })} data-testid={`faq-q-${id}`}
                              className="w-full flex items-center justify-between gap-3 p-4 text-left">
                              <span className="text-sm font-medium">{a}</span>
                              <ChevronDown className={`w-4 h-4 text-jade transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isOpen && <div className="px-4 pb-4 text-sm text-slate-400 leading-relaxed">{b}</div>}
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
